import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import { useNotification } from "../../../../Notifications/NotificationProvider";
import WarehouseManagerCreateModal from "./WarehouseManagerCreateModal";
import SkeletonTable from "../../Common/SkeletonTable";
import ErrorCard from "../../Common/ErrorCard";
import EmptyCard from "../../Common/EmptyCard";
import PaginationBar from "../../Common/PaginationBar";
import { inventoryFetcher } from "../../../network/InventoryService";
import { copyToClipboard } from "../../../../../utilities/helpers";
import { DEFAULT_PAGE_SIZE } from "../../../utils/inventoryHelper";

const WarehouseManagerList = ({
  showWarehouseManagerCreateModal,
  setShowWarehouseManagerCreateModal,
  searchTerm,
}) => {
  const dispatchNotification = useNotification();
  const [page, setPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      user_type: "warehouse_manager",
      page_index: page - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchTerm) {
      params.append("search_term", searchTerm);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/users/api/v1/list?${params.toString()}`;
  }, [page, searchTerm]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const managers = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const handleManagerCreated = () => {
    mutate();
  };

  const handleCopy = async (label, text) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      dispatchNotification({
        type: "SUCCESS",
        message: `${label} copied to clipboard`,
      });
    } else {
      dispatchNotification({
        type: "ERROR",
        message: `Failed to copy ${label}`,
      });
    }
  };

  const handleEditManager = (manager) => {
    setSelectedManager(manager);
    setShowEditModal(true);
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={6}
          rowCount={7}
          headers={[
            "Employee Name",
            "Employee ID",
            "Whitelisting Number",
            "Email",
            "Status",
            "Action",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          onRetryClick={mutate}
          title="Error loading warehouse managers"
          message="Failed to load warehouse manager data. Please try again."
        />
      );
    }

    if (managers.length === 0) {
      return (
        <EmptyCard
          title="No warehouse managers found"
          message={
            searchTerm
              ? "No warehouse managers found matching your search criteria."
              : "No warehouse manager data available. Create your first warehouse manager."
          }
        />
      );
    }

    return (
      <table className="inventory-table">
        <thead>
          <tr>
            <th>Employee Name</th>
            <th>Employee ID</th>
            <th>Whitelisting Number</th>
            <th>Email</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {managers.map((manager) => (
            <tr key={manager.id} className="table-row">
              <td title={manager.name || ""}>{manager.name}</td>
              <td className="employee-id">
                <div
                  className="copyable"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCopy("Employee ID", manager.employee_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCopy("Employee ID", manager.employee_id);
                    }
                  }}
                >
                  {manager.employee_id}
                  <ContentCopyIcon
                    style={{ fontSize: 16, color: "#E2136E", marginLeft: 8 }}
                  />
                </div>
              </td>
              <td className="whitelisting-number">
                <div
                  className="copyable"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    handleCopy("Whitelisting Number", manager.contact_number)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCopy("Whitelisting Number", manager.contact_number);
                    }
                  }}
                >
                  {manager.contact_number}
                  <ContentCopyIcon
                    style={{ fontSize: 16, color: "#E2136E", marginLeft: 8 }}
                  />
                </div>
              </td>
              <td className="email">
                <div
                  className="copyable"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCopy("Email", manager.username)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCopy("Email", manager.username);
                    }
                  }}
                >
                  {manager.username}
                  <ContentCopyIcon
                    style={{ fontSize: 16, color: "#E2136E", marginLeft: 8 }}
                  />
                </div>
              </td>
              <td>
                <span
                  className={`inventory-status-badge ${
                    manager.is_active ? "active" : "inactive"
                  }`}
                >
                  {manager.is_active === undefined
                    ? "N/A"
                    : manager.is_active
                      ? "Active"
                      : "Deactivated"}
                </span>
              </td>
              <td>
                <button
                  className="edit-btn"
                  role="img"
                  aria-label="edit"
                  onClick={() => handleEditManager(manager)}
                >
                  <EditIcon style={{ fontSize: 16, color: "#E2136E" }} />
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="inventory-content-container">
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}

      {showEditModal && selectedManager && (
        <WarehouseManagerCreateModal
          showWarehouseManagerCreateModal={showEditModal}
          setShowWarehouseManagerCreateModal={setShowEditModal}
          isEditable={true}
          managerData={selectedManager}
          isEditMode={true}
          onManagerCreated={handleManagerCreated}
        />
      )}
    </div>
  );
};

export default WarehouseManagerList;
